import React, { useCallback } from 'react';

import { useRouter } from 'expo-router';

import { ISanityVendorConfigs, PickerSelection } from '@rbi-ctg/menu';
import { CmsQuestRequirement } from 'components/quests/cms-hooks/types';
import { IQuest, IQuestStep } from 'components/quests/loyalty-quest/types';
import { canActivateQuest, isStepCompleted } from 'components/quests/loyalty-quest/utils';
import { StepICon } from 'components/step-progress-bar/step-icon';
import { ILoyaltyQuestsUiDetailFragment } from 'generated/sanity-graphql';
import { computeProductCalories } from 'hooks/menu/utils';
import { t } from 'state/intl/translations';
import { useMenuContext } from 'state/menu';
import { primitive } from 'styles/constants';
import { routes } from 'utils/routing';

import { PressableItem, UncompletedStepIcon } from '../styled';

interface IQuestStepProps {
  step: IQuestStep;
  quest: IQuest;
  // @TODO: Add proper type for cmsStep
  cmsStep: any;
  cmsQuestRequirementById: CmsQuestRequirement;
  cmsQuestDetailUI: ILoyaltyQuestsUiDetailFragment;
  onCloseDrawer: () => void;
}

export const QuestStep = ({
  step,
  quest,
  cmsStep,
  cmsQuestRequirementById,
  cmsQuestDetailUI,
  onCloseDrawer,
}: IQuestStepProps) => {
  const { pricingFunction } = useMenuContext();
  const router = useRouter();

  const onPress = useCallback(
    (link?: string, type?: string, id?: string) => () => {
      const path = type && id ? `${routes.menu}/${type}-${id}` : routes.menu;
      onCloseDrawer();
      router.replace(link || path);
    },
    [router, onCloseDrawer]
  );

  const isQuestNotEnrolled = canActivateQuest(quest.status);

  const { cartRequirements, status } = step;
  const isCompletedStep = isStepCompleted(status);
  const isDisabled = isQuestNotEnrolled || isCompletedStep;

  const hasSingleCartRequirement =
    cartRequirements && cartRequirements.length === 1 && cartRequirements[0].length === 1;

  let IconComponent;

  if (isCompletedStep) {
    IconComponent = <StepICon size={'30'} fill={primitive.$crunchyGreen} />;
  } else if (!isQuestNotEnrolled) {
    IconComponent = <UncompletedStepIcon size={'4'} />;
  }

  let itemName: any,
    itemImage: any,
    itemLink: string | null | undefined = null;

  if (cmsStep?.title && cmsStep?.image && cmsStep?.link) {
    itemName = cmsStep.title.localeRaw;
    itemImage = cmsStep.image.locale;
    itemLink = cmsStep.link.localeRaw;
  }

  if (hasSingleCartRequirement) {
    const { id: requirementId } = cartRequirements[0][0];
    const cmsRequirement = cmsQuestRequirementById[requirementId];

    return (
      <PressableItem
        key={step.id}
        onPress={onPress(itemLink || '', cmsRequirement._type, cmsRequirement._id)}
        image={itemImage || cmsRequirement.image}
        altImage={cmsRequirement.imageDescription?.locale ?? ''}
        name={(itemName || cmsRequirement?.name?.locale) ?? ''}
        price={
          pricingFunction({
            item: {
              _type: cmsRequirement._type,
              vendorConfigs: (cmsRequirement.vendorConfigs as ISanityVendorConfigs) || null,
            },
          }) || undefined
        }
        calories={itemName ? undefined : computeProductCalories(cmsRequirement as PickerSelection)}
        icon={IconComponent}
        disabled={isDisabled}
      />
    );
  }

  const name = isCompletedStep
    ? t('questOrderCompleted')
    : itemName || cmsQuestDetailUI.genericStepText?.locale || t('startOrder');

  return (
    <PressableItem
      key={step.id}
      onPress={onPress(itemLink || '')}
      image={itemImage || cmsQuestDetailUI.genericStepImage?.locale}
      name={name}
      icon={IconComponent}
      disabled={isDisabled}
    />
  );
};
